/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { CURRENT_SCHEMA_VERSION, ECSPER_DOCUMENT_NAMESPACE } from './constants.js';
import type { EcsperDocumentContext } from './schema-types.js';

function compareSemver(a: string, b: string): number | null {
  const pa = a.split('.').map((x) => Number.parseInt(x, 10));
  const pb = b.split('.').map((x) => Number.parseInt(x, 10));
  if (pa.some(Number.isNaN) || pb.some(Number.isNaN)) {
    return null;
  }
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) {
      return da > db ? 1 : -1;
    }
  }
  return 0;
}

function isEcsperNamespace(el: Element): boolean {
  if (el.namespaceURI === ECSPER_DOCUMENT_NAMESPACE) {
    return true;
  }
  const xmlns = el.getAttribute('xmlns');
  return xmlns === ECSPER_DOCUMENT_NAMESPACE;
}

/**
 * Locate the `ecsperDocument` root. Tag matching is case-insensitive so tests can run under
 * happy-dom (HTML-oriented DOMParser), whilst Word and browser XML parsers keep canonical casing.
 */
function findEcsperRoot(doc: Document): Element | null {
  const visit = (el: Element | null): Element | null => {
    if (el === null) {
      return null;
    }
    if (el.localName.toLowerCase() === 'ecsperdocument' && isEcsperNamespace(el)) {
      return el;
    }
    for (const child of el.children) {
      const found = visit(child);
      if (found !== null) {
        return found;
      }
    }
    return null;
  };
  return visit(doc.documentElement);
}

function textUnderRoot(root: Element, localName: string): string | null {
  const target = localName.toLowerCase();
  for (const child of root.children) {
    if (child.localName.toLowerCase() === target) {
      const text = child.textContent?.trim();
      return text === undefined || text === '' ? null : text;
    }
  }
  return null;
}

/**
 * Parses ECS-DD-MS365-001 SS B4.8 XML into {@link EcsperDocumentContext}.
 * Unknown child elements are ignored. Missing required elements yield null.
 */
export function parseEcsperDocumentXml(xml: string): EcsperDocumentContext | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'application/xml');
  const parserError = doc.querySelector('parsererror');
  if (parserError !== null) {
    return null;
  }

  const root = findEcsperRoot(doc);
  if (root === null) {
    return null;
  }

  const reportId = textUnderRoot(root, 'reportId');
  const projectId = textUnderRoot(root, 'projectId');
  const organisationId = textUnderRoot(root, 'organisationId');
  const officeId = textUnderRoot(root, 'officeId');
  const residencyRegion = textUnderRoot(root, 'residencyRegion');
  const schemaVersion = textUnderRoot(root, 'schemaVersion');
  const plugin = textUnderRoot(root, 'plugin');
  const openedAt = textUnderRoot(root, 'openedAt');
  const openedBy = textUnderRoot(root, 'openedBy');

  if (
    reportId === null ||
    projectId === null ||
    organisationId === null ||
    officeId === null ||
    residencyRegion === null ||
    schemaVersion === null ||
    plugin === null ||
    openedAt === null ||
    openedBy === null
  ) {
    return null;
  }

  const cmp = compareSemver(schemaVersion, CURRENT_SCHEMA_VERSION);
  if (cmp !== null && cmp > 0) {
    // eslint-disable-next-line no-console -- forward-compatible schema warning (ECS-DD-MS365-001 SS B4.8)
    console.warn(
      `[document-context] schemaVersion ${schemaVersion} is newer than supported ${CURRENT_SCHEMA_VERSION}; parsing known fields only`,
    );
  }

  return {
    reportId,
    projectId,
    organisationId,
    officeId,
    residencyRegion,
    schemaVersion,
    plugin,
    openedAt,
    openedBy,
  };
}
